import type { SuccessResponse } from "@shared/types";
import apiClient from "@/app/api/client";
import { buildingsApi } from "@/modules/objects/api/buildings.api";
import { getSales } from "@/modules/sales/services/sales.backend";
import { endpoints } from "@/shared/api/endpoints";
import type { AppRole } from "@/shared/config/navigation";
import { formatMoney } from "@/shared/utils/money";
import type {
	AdminStatsResponse,
	DashboardKpi,
	DashboardMock,
	DashboardTone,
	DebtsReportResponse,
	ManagerStatsResponse,
	ManagersReportItem,
	ReportsDashboardResponse,
	RoomsReportResponse,
} from "../types/dashboard.types";

const UZS_TO_MLRD = 1_000_000_000;

function toMlrd(value: unknown) {
	const numeric = Number(value ?? 0);
	if (!Number.isFinite(numeric)) {
		return 0;
	}
	return Number((numeric / UZS_TO_MLRD).toFixed(2));
}

type QueryParams = Record<string, string | undefined>;

export const dashboardApi = {
	async getDashboardData(
		role?: AppRole,
		buildingId?: string,
		orgId?: string
	): Promise<DashboardMock> {
		const isSuper = role === "super";
		const params: QueryParams = {};
		if (buildingId && buildingId !== "all") {
			params.buildingId = buildingId;
		}
		if (orgId && orgId !== "all") {
			params.orgId = orgId;
		}
		const [
			dashboardRes,
			adminStatsRes,
			debtsRes,
			managersRes,
			roomsRes,
			salesRes,
			buildingsList,
			orgsList,
		] = await Promise.all([
			apiClient
				.get<SuccessResponse<ReportsDashboardResponse>>(endpoints.reports.dashboard, {
					params,
				})
				.catch(() => ({
					data: { data: {} as ReportsDashboardResponse },
				})),
			isSuper
				? apiClient
						.get<SuccessResponse<AdminStatsResponse>>(endpoints.admin.stats, {
							params: orgId && orgId !== "all" ? { orgId } : {},
						})
						.catch(() => ({ data: { data: {} as AdminStatsResponse } }))
				: Promise.resolve({ data: { data: {} as AdminStatsResponse } }),
			apiClient
				.get<SuccessResponse<DebtsReportResponse>>(endpoints.reports.debts, { params })
				.catch(() => ({
					data: {
						data: {
							totalDebt: 0,
							overdueCount: 0,
							overdueAmount: 0,
							totalClients: 0,
						} as DebtsReportResponse,
					},
				})),
			apiClient
				.get<SuccessResponse<ManagersReportItem[]>>(endpoints.reports.managers, {
					params: orgId && orgId !== "all" ? { orgId } : {},
				})
				.catch(() => ({ data: { data: [] as ManagersReportItem[] } })),
			apiClient
				.get<SuccessResponse<RoomsReportResponse>>(endpoints.reports.rooms, { params })
				.catch(() => ({
					data: {
						data: {
							available: 0,
							sold: 0,
							reserved: 0,
						} as RoomsReportResponse,
					},
				})),
			getSales({ limit: 5 }).catch(() => ({ items: [] })),
			buildingsApi
				.getBuildings({ limit: 100, ...(orgId && orgId !== "all" ? { orgId } : {}) })
				.catch(() => []),
			apiClient
				.get<SuccessResponse<{ items: Array<{ name?: string }> }>>(endpoints.organizationsLegacy)
				.catch(() => ({ data: { data: { items: [] as Array<{ name?: string }> } } })),
		]);

		const reportsDashboard = dashboardRes.data.data;
		const adminStats = adminStatsRes.data.data;
		const debtsReport = debtsRes.data.data;
		const managersReport = managersRes.data.data;
		const roomsReport = roomsRes.data.data;
		const recentSalesList = salesRes.items || [];
		const buildings = buildingsList || [];
		const orgs = orgsList.data.data.items;

		// Dropdowns
		const buildingOptions = ["Barcha binolar", ...buildings.map((b: { name: string }) => b.name)];
		const organizationOptions = [
			"Barcha tashkilotlar",
			...orgs.map((o: { name?: string }) => o.name || ""),
		];

		// KPIs
		const kpis = isSuper
			? [
					{
						id: "total-orgs",
						title: "Tashkilotlar",
						value: String(adminStats.totalOrganizations || 0),
						unit: "ta",
						note: "tizimda ro'yxatdan o'tgan",
						tone: "blue" as const,
						icon: "sales" as const,
					},
					{
						id: "total-users",
						title: "Foydalanuvchilar",
						value: String(adminStats.totalUsers || 0),
						unit: "ta",
						note: "faol xodimlar",
						tone: "green" as const,
						icon: "activeSales" as const,
					},
					{
						id: "total-buildings",
						title: "Binolar soni",
						value: String(adminStats.totalBuildings || 0),
						unit: "ta",
						note: "barcha ob'ektlar",
						tone: "sky" as const,
						icon: "revenue" as const,
					},
					{
						id: "total-sales",
						title: "Jami sotuv",
						value: String(adminStats.totalSales || 0),
						unit: "ta",
						note: "jami shartnomalar",
						tone: "violet" as const,
						icon: "payments" as const,
					},
				]
			: [
					{
						id: "monthly-sales",
						title: "Bu oy sotuvlar",
						value: String(reportsDashboard.summary?.totalSalesThisMonth ?? 0),
						unit: "ta",
						note: "jami sotuv",
						tone: "blue" as const,
						icon: "sales" as const,
						trend: "up" as const,
					},
					{
						id: "monthly-revenue",
						title: "Bu oy daromad",
						value: formatMoney(reportsDashboard.summary?.revenueThisMonth ?? 0),
						unit: "UZS",
						note: "jami portfel",
						tone: "green" as const,
						icon: "revenue" as const,
						trend: "up" as const,
					},
					{
						id: "active-sales",
						title: "Aktiv sotuvlar",
						value: String(reportsDashboard.summary?.totalActiveSales ?? 0),
						unit: "ta",
						note: "faol shartnomalar",
						tone: "sky" as const,
						icon: "activeSales" as const,
					},
					{
						id: "collected-payments",
						title: "Yig'ilgan to'lov",
						value: formatMoney(reportsDashboard.summary?.totalCollected ?? 0),
						unit: "UZS",
						note: "kassa yig'imi",
						tone: "violet" as const,
						icon: "payments" as const,
					},
				];

		// Month labels for fallback
		const months = [
			"Yan",
			"Fev",
			"Mar",
			"Apr",
			"May",
			"Iyun",
			"Iyul",
			"Avg",
			"Sen",
			"Okt",
			"Noy",
			"Dek",
		];
		const last6MonthsLabels: string[] = [];
		const now = new Date();
		for (let i = 5; i >= 0; i--) {
			const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
			last6MonthsLabels.push(months[d.getMonth()]);
		}

		const dashboardBarChart = reportsDashboard.barChart ?? [];
		const dashboardLineChart = reportsDashboard.lineChart ?? [];
		const dashboardMonthlyDynamics = reportsDashboard.monthlyDynamics ?? [];

		type BarChartItem = { buildingName?: string; kirim?: number };
		const revenueByMonth = dashboardBarChart.length
			? {
					labels: dashboardBarChart.map((item: BarChartItem) => item.buildingName ?? ""),
					values: dashboardBarChart.map((item: BarChartItem) => toMlrd(item.kirim)),
				}
			: { labels: last6MonthsLabels, values: [] };

		type LineChartItem = {
			month?: string;
			collected?: number;
			plan?: number;
			kirim?: number;
			chiqim?: number;
		};
		const lineSource: LineChartItem[] = dashboardMonthlyDynamics.length
			? dashboardMonthlyDynamics
			: dashboardLineChart;
		const monthlyDynamics = lineSource.length
			? {
					labels: lineSource.map((item: LineChartItem) => item.month ?? ""),
					collected: lineSource.map((item: LineChartItem) => toMlrd(item.collected ?? item.kirim)),
					plan: lineSource.map((item: LineChartItem) => toMlrd(item.plan ?? item.chiqim)),
				}
			: { labels: last6MonthsLabels, collected: [], plan: [] };

		const roomStatuses = [
			{ label: "Sotuvda", value: roomsReport.available || 0, color: "#0f9d58" },
			{ label: "Sotilgan", value: roomsReport.sold || 0, color: "#2756d8" },
			{ label: "Band", value: roomsReport.reserved || 0, color: "#cc8a00" },
		];

		const getInitials = (name: string) =>
			name
				.split(" ")
				.map((p) => p[0] || "")
				.join("")
				.slice(0, 2)
				.toUpperCase();
		const tones: DashboardTone[] = ["sky", "green", "violet", "amber", "blue"];

		const managers = managersReport.map((m: ManagersReportItem, idx: number) => {
			const fullName = m.fullName || "Menejer";

			return {
				id: m.userId,
				name: fullName,
				initials: getInitials(fullName),
				salesCount: `${m.salesCount} ta`,
				amount: `${formatMoney(m.totalRevenue)} UZS`,
				tone: tones[idx % tones.length],
			};
		});

		const upcomingCount = reportsDashboard.upcomingPayments?.count ?? 0;
		const upcomingAmount = reportsDashboard.upcomingPayments?.totalAmount ?? 0;

		const overdueCount = reportsDashboard.overduePayments?.count ?? 0;
		const overdueAmount = reportsDashboard.overduePayments?.totalAmount ?? 0;

		const debtorCount = debtsReport.totalDebtors ?? debtsReport.totalClients ?? overdueCount ?? 0;
		const debtAmount = debtsReport.totalDebt ?? overdueAmount ?? 0;

		const payments = {
			dueCount: `${upcomingCount} ta`,
			dueAmount: `${formatMoney(upcomingAmount)} UZS`,
			debtorCount: `${debtorCount} ta`,
			debtAmount: `${formatMoney(debtAmount)} UZS`,
		};

		const recentSales = recentSalesList.map(
			(sale: { id: string; clientName: string; roomNumber: string; finalPrice: number }) => ({
				id: sale.id,
				clientName: sale.clientName,
				room: `#${sale.roomNumber}`,
				amount: `${formatMoney(sale.finalPrice)} UZS`,
			})
		);

		return {
			buildingOptions,
			buildingItems: buildings,
			organizationOptions,
			organizationItems: orgs,
			kpis,
			revenueByMonth,
			monthlyDynamics,
			roomStatuses,
			managers,
			payments,
			recentSales,
		};
	},

	async getManagerDashboardData(
		userId: string
	): Promise<{ kpis: DashboardKpi[]; sales: Record<string, unknown>[] }> {
		const [statsRes, salesRes] = await Promise.all([
			apiClient.get<SuccessResponse<ManagerStatsResponse>>(endpoints.users.stats(userId)),
			getSales({ managerId: userId, limit: 10 }).catch(() => ({ items: [] })),
		]);

		const stats = statsRes.data.data;
		const items = salesRes.items || [];

		const kpis = [
			{
				id: "my-sales",
				title: "Mening sotuvlarim",
				value: String(stats.totalSales || 0),
				unit: "ta",
				note: "jami sotuv",
				tone: "blue" as const,
				icon: "sales" as const,
			},
			{
				id: "this-month",
				title: "Bu oy",
				value: String(stats.byMonth?.[0]?.sales || 0),
				unit: "ta",
				note: "oylik sotuvlar",
				tone: "green" as const,
				icon: "activeSales" as const,
				trend: "up" as const,
			},
			{
				id: "total-amount",
				title: "Jami summa",
				value: formatMoney(stats.totalRevenue),
				unit: "UZS",
				note: "shartnomalar summasi",
				tone: "sky" as const,
				icon: "revenue" as const,
			},
			{
				id: "collected",
				title: "Yig'ilgan",
				value: formatMoney(stats.totalCollected),
				unit: "UZS",
				note: "to'lovlar yig'imi",
				tone: "violet" as const,
				icon: "payments" as const,
			},
		];

		const getInitials = (name: string) =>
			name
				.split(" ")
				.map((p) => p[0] || "")
				.join("")
				.slice(0, 2)
				.toUpperCase();
		const tones = ["blue", "green", "sky", "violet", "amber"];

		const sales = items.map(
			(
				sale: {
					id: string;
					clientName: string;
					roomNumber: string;
					buildingName: string;
					finalPrice: number;
					paidPercent: number;
					status: string;
				},
				idx: number
			) => ({
				id: sale.id,
				initials: getInitials(sale.clientName),
				client: sale.clientName,
				room: sale.roomNumber,
				building: sale.buildingName,
				amount: `${formatMoney(sale.finalPrice)} UZS`,
				paid: sale.paidPercent || 0,
				status: sale.status === "faol" ? "Faol" : "Yakunlangan",
				tone: tones[idx % tones.length],
			})
		);

		return {
			kpis,
			sales,
		};
	},
};
